import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cardOptions, contrast, fontCss, kitColors, mainInk, swipeUnder, swipeWord, textShade } from './docShared';

const contrastInk = (bg) => (contrast('#111111', bg) >= contrast('#FFFFFF', bg) ? '#111111' : '#FFFFFF');

// The "monitor": a 16:9 frame that plays a few sample moments of a documentary in the chosen look,
// with the look's real typefaces, colours, panels and text reveal. What you see is what the engine draws.

const SCENES = [
  { key: 'title', label: 'Title' },
  { key: 'caption', label: 'Name caption' },
  { key: 'number', label: 'Big number' },
  { key: 'quote', label: 'Quote' },
  { key: 'card', label: 'Text card' },
  { key: 'plain', label: 'Plain screen' },
];

// the "footage" behind the graphics: a lit film still, tinted to suit each look
const MOODS = {
  cinematic: 'radial-gradient(120% 90% at 78% 18%, rgba(236,186,120,.55), transparent 55%), radial-gradient(90% 70% at 12% 100%, rgba(40,26,14,.9), transparent 70%), linear-gradient(160deg, #3b2a1c 0%, #17110b 55%, #0a0806 100%)',
  explainer: 'radial-gradient(90% 80% at 85% 10%, rgba(58,134,255,.55), transparent 60%), radial-gradient(70% 60% at 0% 100%, rgba(255,210,63,.18), transparent 70%), linear-gradient(150deg, #1b2440 0%, #0f1424 60%, #090c16 100%)',
  editorial: 'radial-gradient(110% 80% at 70% 20%, rgba(214,201,170,.5), transparent 60%), linear-gradient(170deg, #5b5446 0%, #2c2922 60%, #191712 100%)',
  newsroom: 'radial-gradient(90% 70% at 20% 10%, rgba(160,175,190,.35), transparent 60%), radial-gradient(60% 50% at 95% 95%, rgba(227,52,47,.18), transparent 70%), linear-gradient(165deg, #2a2f35 0%, #15181b 60%, #0b0c0e 100%)',
  tech: 'radial-gradient(80% 70% at 80% 15%, rgba(59,224,255,.35), transparent 60%), radial-gradient(70% 60% at 5% 90%, rgba(139,124,255,.3), transparent 70%), linear-gradient(160deg, #0b1d33 0%, #07111c 60%, #04080e 100%)',
  organic: 'radial-gradient(110% 80% at 75% 15%, rgba(244,184,96,.5), transparent 58%), radial-gradient(80% 60% at 10% 100%, rgba(167,201,87,.28), transparent 70%), linear-gradient(160deg, #3a3a22 0%, #1f2416 60%, #12160c 100%)',
  noir: 'radial-gradient(70% 90% at 80% 20%, rgba(220,220,220,.28), transparent 55%), repeating-linear-gradient(100deg, rgba(0,0,0,.0) 0 22px, rgba(0,0,0,.35) 22px 40px), linear-gradient(170deg, #2b2b2d 0%, #121213 60%, #050505 100%)',
  retro: 'radial-gradient(60% 50% at 50% 62%, rgba(255,60,172,.55), transparent 70%), linear-gradient(180deg, #1a0b38 0%, #3b0f4f 55%, #0b0414 56%, #120822 100%)',
  gallery: 'radial-gradient(60% 70% at 70% 30%, rgba(200,169,107,.28), transparent 60%), linear-gradient(160deg, #2a2620 0%, #14120f 60%, #070605 100%)',
  playground: 'radial-gradient(40% 40% at 80% 25%, rgba(255,201,60,.6), transparent 70%), radial-gradient(50% 50% at 15% 85%, rgba(255,111,145,.45), transparent 70%), linear-gradient(160deg, #3b3fb0 0%, #2b2d6e 60%, #1a1b4e 100%)',
  newsreel: 'radial-gradient(90% 80% at 50% 40%, rgba(224,176,76,.3), transparent 60%), linear-gradient(170deg, #4a3b26 0%, #251c11 60%, #0f0b06 100%)',
  arena: 'radial-gradient(50% 40% at 50% 0%, rgba(255,255,255,.45), transparent 70%), radial-gradient(70% 50% at 50% 100%, rgba(43,234,110,.3), transparent 70%), linear-gradient(180deg, #1c2b3d 0%, #0d1620 60%, #04080c 100%)',
};

const GRAIN = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='.55'/></svg>\")";

function hexA(hex, a) {
  const h = (hex || '#000000').replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

function panelStyle(look, c) {
  const s = look.surface || {};
  const base = { color: c.panel_text, borderRadius: `${(s.radius || 0) * 0.06}cqw` };
  switch (s.style) {
    case 'solid':
      return { ...base, background: hexA(c.panel, s.panel_alpha ?? 0.9), boxShadow: '0 .8cqw 2.4cqw rgba(0,0,0,.35)' };
    case 'paper':
      return { ...base, background: c.panel, backgroundImage: `${GRAIN}`, backgroundBlendMode: 'multiply',
        boxShadow: '0 .6cqw 1.8cqw rgba(0,0,0,.45)', transform: `rotate(${-(s.tilt || 0)}deg)` };
    case 'sharp':
      return { ...base, borderRadius: 0, background: hexA(c.panel, s.panel_alpha ?? 0.85),
        borderLeft: `.45cqw solid ${c.accent}` };
    case 'glass':
      return { ...base, background: hexA(c.panel, s.panel_alpha ?? 0.55), backdropFilter: 'blur(1.2cqw)',
        border: `1px solid ${hexA(c.accent, 0.45)}`, boxShadow: `0 0 2.4cqw ${hexA(c.accent, (s.glow || 0) * 0.35)}` };
    case 'soft':
      return { ...base, background: hexA(c.panel, s.panel_alpha ?? 0.5), backdropFilter: 'blur(.6cqw)' };
    default:
      return { color: c.on_footage || c.text, background: 'transparent' };
  }
}

function textShadow(look) {
  return look.surface?.shadow ? '0 .2cqw 1.6cqw rgba(0,0,0,.55)' : 'none';
}

/** Text that arrives the way this look reveals text. */
function Reveal({ text, kind, dur = 0.8, delay = 0, style, className = '' }) {
  const [shown, setShown] = useState(kind === 'decode' ? '' : text);
  const reduce = useRef(typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

  useEffect(() => {
    if (kind !== 'decode' || reduce.current) { setShown(text); return undefined; }
    const glyphs = '#%&*+=<>/\\0123456789ABCDEF';
    const total = Math.max(8, Math.round((dur * 1000) / 40));
    let i = 0;
    let t0 = null;
    const start = setTimeout(() => {
      t0 = setInterval(() => {
        i += 1;
        const done = Math.floor((i / total) * text.length);
        setShown(text.split('').map((ch, j) => (j < done || ch === ' ' ? ch
          : glyphs[Math.floor(Math.random() * glyphs.length)])).join(''));
        if (i >= total) { clearInterval(t0); setShown(text); }
      }, 40);
    }, delay * 1000);
    return () => { clearTimeout(start); if (t0) clearInterval(t0); };
  }, [text, kind, dur, delay]);

  const anim = { animationDuration: `${dur}s`, animationDelay: `${delay}s` };
  if (kind === 'type') anim.animationTimingFunction = `steps(${Math.max(4, text.length)}, end)`;
  return (
    <span className={`lm-reveal lm-reveal--${kind} ${className}`} style={{ ...anim, ...style }}>
      {shown}
    </span>
  );
}

function Rule({ look, c, delay }) {
  const rule = look.surface?.rule;
  if (!rule || rule === 'none') return null;
  const st = { animationDelay: `${delay}s`, background: rule === 'marker' ? c.accent2 : c.accent };
  return <span className={`lm-rule lm-rule--${rule}`} style={st} />;
}

function SceneTitle({ look, c, title, channelName }) {
  const f = look.fonts;
  const m = look.motion;
  const onPaper = look.surface?.style === 'paper';
  const ink = onPaper ? c.panel_text : (c.on_footage || c.text);
  const body = (
    <>
      <Reveal text={channelName ? `A ${channelName} documentary` : 'A documentary'} kind="fade_up"
              dur={m.enter_sec * 0.8} style={{ ...fontCss(f.label), color: c.accent, fontSize: '1.45cqw' }} />
      <Reveal text={title} kind={m.reveal} dur={m.enter_sec * 1.4} delay={m.stagger_sec * 3}
              className="lm-title" style={{ ...fontCss(f.display), color: ink, textShadow: onPaper ? 'none' : textShadow(look) }} />
      <Rule look={look} c={c} delay={m.enter_sec * 1.2} />
    </>
  );
  return (
    <div className="lm-scene lm-scene--title">
      {onPaper ? <div className="lm-card" style={panelStyle(look, c)}>{body}</div> : body}
    </div>
  );
}

function SceneCaption({ look, c }) {
  const f = look.fonts;
  const m = look.motion;
  const plain = (look.surface?.style || 'none') === 'none';
  return (
    <div className="lm-scene lm-scene--caption">
      <div className={`lm-lower ${plain ? 'lm-lower--plain' : ''}`} style={panelStyle(look, c)}>
        {plain && <span className="lm-lower__bar" style={{ background: c.accent }} />}
        <Reveal text="Margaret Ellis" kind={m.reveal} dur={m.enter_sec} delay={m.stagger_sec * 2}
                style={{ ...fontCss(f.heading), fontSize: '2.6cqw', textShadow: plain ? textShadow(look) : 'none' }} />
        <Reveal text="Historian, Columbia University" kind="fade_up" dur={m.enter_sec * 0.8}
                delay={m.stagger_sec * 5 + 0.2}
                style={{ ...fontCss(f.label), fontSize: '1.3cqw',
                         // on the brand's text box the engine swaps a small accent line that would not read (≥ 4.5)
                         color: !plain && c.panel_chosen && contrast(c.accent, c.panel) < 4.5 ? c.panel_text : c.accent,
                         opacity: 0.95 }} />
      </div>
    </div>
  );
}

function SceneNumber({ look, c, hl }) {
  const f = look.fonts;
  const m = look.motion;
  const ink = c.on_footage || c.text;
  const hand = ['marker', 'soft'].includes(look.surface?.annotate);
  return (
    <div className="lm-scene lm-scene--number">
      <Reveal text="$26 billion" kind={m.reveal} dur={m.enter_sec * 1.1}
              style={{ ...fontCss(f.number), color: c.accent, fontSize: '9cqw', textShadow: textShadow(look) }} />
      <span className="lm-reveal lm-reveal--fade_up"
            style={{ ...fontCss(f.body), color: ink, fontSize: '1.9cqw', textShadow: textShadow(look),
                     animationDuration: `${m.enter_sec * 0.8}s`, animationDelay: `${m.stagger_sec * 4 + 0.25}s` }}>
        lost on the stock market in a{' '}
        <span className={`lm-hl lm-hl--${hl || 'color'}`}
              style={{ '--hl': c.accent, '--hl2': c.accent2, animationDelay: `${m.enter_sec * 1.2 + 0.3}s`,
                       // a block highlight: the word in whichever ink reads on the accent block
                       ...(hl === 'block' ? { color: contrast('#111111', c.accent) >= contrast('#FFFFFF', c.accent) ? '#111111' : '#FFFFFF' } : {}) }}>
          single day
        </span>
      </span>
      <span className="lm-track" style={{ background: hexA(ink, 0.18) }}>
        <span className="lm-track__fill" style={{ background: c.accent, animationDuration: `${m.enter_sec * 1.6}s`,
                                                   animationDelay: `${m.enter_sec * 0.6}s` }} />
      </span>
      {hand && (
        <Reveal text="one day!" kind="fade_up" dur={0.5} delay={m.enter_sec * 1.6} className="lm-hand"
                style={{ ...fontCss(f.hand), color: look.surface?.annotate === 'marker' ? c.accent : c.accent2,
                         fontSize: '2.4cqw' }} />
      )}
    </div>
  );
}

function SceneQuote({ look, c, quote }) {
  const f = look.fonts;
  const m = look.motion;
  const plain = (look.surface?.style || 'none') === 'none';
  return (
    <div className="lm-scene lm-scene--quote">
      <div className="lm-quote" style={plain ? {} : { ...panelStyle(look, c), transform: 'none' }}>
        <span className="lm-quote__mark" style={{ color: c.accent, fontFamily: fontCss(f.quote).fontFamily }}>“</span>
        <Reveal text={quote} kind={m.reveal === 'slam' ? 'fade_up' : m.reveal} dur={m.enter_sec * 1.6}
                delay={m.stagger_sec * 2}
                style={{ ...fontCss(f.quote), color: plain ? (c.on_footage || c.text) : c.panel_text,
                         fontSize: '2.7cqw', textShadow: plain ? textShadow(look) : 'none' }} />
        <Reveal text="From your script" kind="fade_up" dur={m.enter_sec * 0.7} delay={m.enter_sec * 1.8}
                style={{ ...fontCss(f.label), color: c.accent, fontSize: '1.2cqw' }} />
      </div>
    </div>
  );
}

/** A text card on the brand's ONE background: the brand inks that read there, the accent, a highlighted word. */
function SceneCard({ look, c, hl }) {
  const f = look.fonts;
  const m = look.motion;
  const style = look.highlight || 'color';
  const blockInk = contrastInk(c.accent);
  const word = {
    block: { background: c.accent, color: blockInk, padding: '0 .4cqw' },
    swipe: { background: swipeUnder(c.accent2, c.card), color: swipeWord(c.card_text, c.accent2, c.card)[0],
             padding: '0 .4cqw' },
    underline: { color: c.card_text, boxShadow: `inset 0 -.35cqw 0 ${c.accent}` },
    color: { color: textShade(c.accent, c.card) },
  }[style] || { color: textShade(c.accent, c.card) };
  return (
    <div className="lm-scene lm-scene--card">
      <Reveal text="Chapter two · 1929" kind="fade_up" dur={m.enter_sec * 0.8}
              style={{ ...fontCss(f.label), color: c.card_soft, fontSize: '1.4cqw' }} />
      <span className="lm-reveal lm-reveal--fade_up lm-card-title"
            style={{ ...fontCss(f.display), color: c.card_text, fontSize: '4.4cqw', textTransform: 'none',
                     animationDuration: `${m.enter_sec}s`, animationDelay: `${m.stagger_sec * 2}s` }}>
        The <span style={word}>last</span> ordinary day
      </span>
      <Reveal text="$26 billion" kind="fade_up" dur={m.enter_sec} delay={m.stagger_sec * 4 + 0.2}
              style={{ ...fontCss(f.number), color: c.accent, fontSize: '6cqw' }} />
      <Reveal text="Source: New York Stock Exchange records" kind="fade_up" dur={m.enter_sec * 0.8}
              delay={m.stagger_sec * 6 + 0.3} style={{ ...fontCss(f.body), color: c.card_soft, fontSize: '1.35cqw' }} />
    </div>
  );
}

/** The standard background: what shows when no picture does — a chart on the plain screen. */
function ScenePlain({ look, c }) {
  const f = look.fonts;
  const m = look.motion;
  const bars = [0.42, 0.66, 0.9];
  return (
    <div className="lm-scene lm-scene--plain">
      <Reveal text="Farm size, in acres" kind="fade_up" dur={m.enter_sec * 0.8}
              style={{ ...fontCss(f.heading), color: c.ground_text, fontSize: '2.4cqw', textTransform: 'none' }} />
      <div className="lm-pbars">
        {bars.map((v, i) => (
          <div key={i} className="lm-prow">
            <span style={{ ...fontCss(f.label), color: c.ground_soft, fontSize: '1.3cqw' }}>{['1950', '1990', '2020'][i]}</span>
            <span className="lm-pbar" style={{ width: `${v * 70}%`, background: i === 2 ? c.accent : c.ground_soft,
                                                animationDelay: `${0.2 + i * m.stagger_sec * 2}s` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function firstSentence(script) {
  const s = (script || '').replace(/\s+/g, ' ').trim();
  if (!s) return 'Nobody on the trading floor that morning knew it would be the last ordinary day.';
  const m = s.match(/^(.{20,140}?[.!?])(\s|$)/);
  const out = m ? m[1] : s.slice(0, 120);
  return out.length > 130 ? `${out.slice(0, 127)}…` : out;
}

const AXES = ['tempo', 'reveal', 'entrance', 'highlight', 'treatment', 'surface'];
const TEMPO = { settle: 1, brisk: 0.72, glide: 1.3, spring: 0.85 };
const RADIUS = { none: 0, solid: 12, glass: 10, paper: 1, soft: 20, sharp: 0 };

/** What this preview moment shows: the brand kit's picks (or the look's whole range), rotated scene to scene. */
function pickDesign(look, motion, demo, idx) {
  const d = {};
  AXES.forEach((ax, j) => {
    const vals = motion?.[ax]?.length ? motion[ax] : (look.axes?.[ax] || []);
    if (vals.length) d[ax] = vals[(idx + j) % vals.length];
  });
  if (demo?.axis) d[demo.axis] = demo.value;
  return d;
}

function styledLook(look, d) {
  const mul = TEMPO[d.tempo] || 1;
  const surface = { ...look.surface };
  if (d.surface) {
    surface.style = d.surface;
    surface.radius = RADIUS[d.surface] ?? surface.radius;
    if (['paper', 'solid'].includes(d.surface) && (surface.panel_alpha ?? 1) < 0.85) surface.panel_alpha = 0.92;
    if (d.surface === 'paper' && !surface.tilt) surface.tilt = 1.2;
  }
  return { ...look, surface,
    motion: { ...look.motion, reveal: d.reveal || look.motion.reveal, enter_sec: look.motion.enter_sec * mul,
              stagger_sec: look.motion.stagger_sec * mul } };
}

export default function LookMonitor({ lookName, look, kit, title, script, channelName, autoplay = true,
                                      compact = false, scene: forcedScene = null, onScene, demo = null,
                                      axisLabels = null, cardPing = 0, place = null }) {
  const [idx, setIdx] = useState(0);
  const [hover, setHover] = useState(false);
  const c = useMemo(() => kitColors(look, kit), [look, kit]);
  const quote = useMemo(() => firstSentence(script), [script]);
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const current = forcedScene ?? idx;
  const [demoOn, setDemoOn] = useState(null);
  // where titles, numbers and quotes are shown: on the brand's own colours (a text-card scene, as the engine draws
  // it) or over a picture — the owner asked to SEE the chosen colours, so "your colours" comes first
  const [whereState, setWhere] = useState('brand');
  const where = place || whereState; // a tile of the colour board fixes it

  // a background was just picked: show it on the text card
  useEffect(() => { if (cardPing) setIdx(Math.max(0, SCENES.findIndex((x) => x.key === (cardPing.scene || 'card')))); }, [cardPing]);

  // a clicked option plays on the current moment, then the preview goes back to rotating
  useEffect(() => {
    if (!demo) return undefined;
    setDemoOn(demo);
    const t = setTimeout(() => setDemoOn(null), 4200);
    return () => clearTimeout(t);
  }, [demo]);

  useEffect(() => {
    if (!autoplay || hover || reduce || forcedScene != null || demoOn) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % SCENES.length), 3600);
    return () => clearInterval(t);
  }, [autoplay, hover, reduce, forcedScene, demoOn]);

  if (!look) return <div className="lm lm--empty" />;
  const d = pickDesign(look, kit?.motion, demoOn, current);
  let lk = styledLook(look, d);
  if (c.panel_chosen) { // the brand chose a text-display background: text over pictures sits in it (as the engine draws)
    lk = { ...lk, surface: { ...lk.surface, style: lk.surface.style === 'none' ? 'solid' : lk.surface.style,
                             panel_alpha: Math.max(lk.surface.panel_alpha ?? 0.9, 0.88) } };
  }
  const key = SCENES[current]?.key;
  // title / big number / quote "on your colours": the text-card colour as the ground, the brand inks that read on
  // it, and any card or panel in the card colour that stands out from it (render.assign_grounds does the same)
  const onBrand = where === 'brand' && ['title', 'number', 'quote'].includes(key);
  let cc = c;
  if (onBrand) {
    const cardOn = cardOptions(look, kit?.accent, c.card)[0] || c.card;
    // small words in the accent take its nearest readable shade on the card colour (the engine's ground fix)
    // (on the card colour, or on the inner card when the scene puts its words in one)
    const style = lk.surface?.style || 'none';
    const inPanel = (key === 'quote' && style !== 'none') || (key === 'title' && style === 'paper');
    cc = { ...c, on_footage: c.card_text, text: c.card_text, text_soft: c.card_soft, panel: cardOn,
           panel_text: mainInk(cardOn, look), accent: textShade(c.accent, inPanel ? cardOn : c.card) };
    lk = { ...lk, surface: { ...lk.surface, shadow: false, panel_alpha: 1 } };
  }
  const sceneKey = `${lookName}-${current}-${where}-${c.accent}-${c.background}-${c.card}-${c.panel}-${JSON.stringify(d)}-${demoOn?.n || 0}`;
  const props = { look: lk, c: cc, title: title || 'The Night the Banks Closed', channelName, quote, hl: d.highlight };
  const Scene = [SceneTitle, SceneCaption, SceneNumber, SceneQuote, SceneCard, ScenePlain][current];
  const onCard = key === 'card' || key === 'plain' || onBrand;
  const screenBg = key === 'plain' ? c.background : c.card;
  const mul = TEMPO[d.tempo] || 1;
  const label = (ax) => axisLabels?.[ax]?.options?.[d[ax]] || d[ax];

  return (
    <div className={`lm ${compact ? 'lm--compact' : ''}`} onMouseEnter={() => setHover(true)}
         onMouseLeave={() => setHover(false)}>
      <div className="lm-screen" style={{ background: onCard ? screenBg : (MOODS[lookName] || MOODS.cinematic),
                                         transition: 'background .4s' }}>
        {!onCard && <div className="lm-grain" style={{ backgroundImage: GRAIN, opacity: 0.08 + (look.surface?.grain || 0) }} />}
        {!onCard && <div className="lm-vignette" style={{ opacity: Math.min(1, 0.45 + (look.surface?.grain || 0) * 2) }} />}
        <div className={`lm-stage lm-tx--${d.treatment || 'clean'} ${d.tempo === 'spring' ? 'lm-spring' : ''}`}
             key={sceneKey} style={{ '--lm-accent': c.accent, '--lm-accent2': c.accent2 }}>
          <div className={`lm-ent lm-ent--${d.entrance || 'own'}`}
               style={{ animationDuration: `${Math.max(0.35, look.motion.enter_sec * mul * 0.9)}s` }}>
            <Scene {...props} />
          </div>
          {['grain', 'halftone', 'scan'].includes(d.treatment) && <div className={`lm-tex lm-tex--${d.treatment}`} />}
        </div>
        <div className="lm-safe" aria-hidden="true" />
      </div>
      {!compact && (
        <div className="lm-bar">
          <div className="lm-where" role="group" aria-label="Show titles, numbers and quotes">
            <button type="button" className={`lm-scenebtn ${where === 'brand' ? 'is-on' : ''}`} aria-pressed={where === 'brand'}
                    onClick={() => setWhere('brand')}>On your colours</button>
            <button type="button" className={`lm-scenebtn ${where === 'picture' ? 'is-on' : ''}`} aria-pressed={where === 'picture'}
                    onClick={() => setWhere('picture')}>Over a picture</button>
          </div>
          <div className="lm-scenes" role="tablist" aria-label="Preview moments">
            {SCENES.map((s, i) => (
              <button key={s.key} type="button" role="tab" aria-selected={current === i}
                      className={`lm-scenebtn ${current === i ? 'is-on' : ''}`}
                      onClick={() => { setIdx(i); onScene?.(i); }}>
                {s.label}
              </button>
            ))}
          </div>
          {axisLabels && (
            <dl className="lm-now" aria-live="polite">
              {['tempo', 'reveal', 'entrance', 'highlight', 'treatment', 'surface'].filter((ax) => d[ax]).map((ax) => (
                <div key={ax} className={demoOn?.axis === ax ? 'is-demo' : ''}>
                  <dt>{axisLabels?.[ax]?.title || ax}</dt><dd>{label(ax)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </div>
  );
}
